"use strict";
//
// Copyright © 2020, 2021 Anticrm Platform Contributors.
// Copyright © 2021 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//
Object.defineProperty(exports, "__esModule", { value: true });
exports.isPredicate = isPredicate;
exports.createPredicates = createPredicates;
const objvalue_1 = require("./objvalue");
const utils_1 = require("./utils");
const fast_equals_1 = require("fast-equals");
function execPredicate(docs, propertyKey, pred) {
    const result = [];
    for (const doc of docs) {
        const value = (0, objvalue_1.getObjectValue)(propertyKey, doc);
        if (pred(value)) {
            result.push(doc);
        }
    }
    return result;
}
const predicates = {
    $in: (o, propertyKey) => {
        if (!Array.isArray(o)) {
            throw new Error('$in predicate requires array');
        }
        return (docs) => execPredicate(docs, propertyKey, (value) => {
            if (Array.isArray(value)) {
                return o.some((p) => value.includes(p));
            }
            else {
                // eslint-disable-next-line eqeqeq
                return o.some((p) => p == value);
            }
        });
    },
    $all: (o, propertyKey) => {
        if (!Array.isArray(o)) {
            throw new Error('$all predicate requires array');
        }
        return (docs) => execPredicate(docs, propertyKey, (value) => {
            for (const val of o) {
                if (!value.includes(val))
                    return false;
            }
            return true;
        });
    },
    $nin: (o, propertyKey) => {
        if (!Array.isArray(o)) {
            throw new Error('$nin predicate requires array');
        }
        return (docs) => execPredicate(docs, propertyKey, (value) => {
            if (Array.isArray(value)) {
                return !o.some((p) => value.includes(p));
            }
            else {
                // eslint-disable-next-line eqeqeq
                return !o.some((p) => p == value);
            }
        });
    },
    $like: (query, propertyKey) => {
        const searchString = query
            .split('%')
            .map((it) => (0, utils_1.escapeLikeForRegexp)(it))
            .join('.*');
        const regex = RegExp(`^${searchString}$`, 'i');
        return (docs) => execPredicate(docs, propertyKey, (value) => regex.test(value));
    },
    $regex: (o, propertyKey) => {
        const re = new RegExp(o.$regex, o.$options);
        return (docs) => execPredicate(docs, propertyKey, (value) => value.match(re) !== null);
    },
    $gt: (o, propertyKey) => {
        return (docs) => execPredicate(docs, propertyKey, (value) => value > o);
    },
    $gte: (o, propertyKey) => {
        return (docs) => execPredicate(docs, propertyKey, (value) => value >= o);
    },
    $lt: (o, propertyKey) => {
        return (docs) => execPredicate(docs, propertyKey, (value) => value < o);
    },
    $lte: (o, propertyKey) => {
        return (docs) => execPredicate(docs, propertyKey, (value) => value <= o);
    },
    $exists: (o, propertyKey) => {
        return (docs) => execPredicate(docs, propertyKey, (value) => (value !== undefined) === o);
    },
    $ne: (o, propertyKey) => {
        // eslint-disable-next-line eqeqeq
        return (docs) => execPredicate(docs, propertyKey, (value) => (o != null ? !(0, fast_equals_1.deepEqual)(o, value) : value != null));
    },
    $size: (o, propertyKey) => {
        return (docs) => execPredicate(docs, propertyKey, (value) => {
            if (value == null) {
                return false;
            }
            if (!Array.isArray(value)) {
                throw new Error('$size predicate requires array');
            }
            if (typeof o === 'number') {
                return value.length === o;
            }
            if (typeof o === 'object' && o.$gt !== undefined) {
                return value.length > o.$gt;
            }
            if (typeof o === 'object' && o.$gte !== undefined) {
                return value.length >= o.$gte;
            }
            if (typeof o === 'object' && o.$lt !== undefined) {
                return value.length < o.$lt;
            }
            if (typeof o === 'object' && o.$lte !== undefined) {
                return value.length <= o.$lte;
            }
            return false;
        });
    }
};
function isPredicate(o) {
    if (o === null || typeof o !== 'object') {
        return false;
    }
    const keys = Object.keys(o);
    return keys.length > 0 && keys.every((key) => key.startsWith('$'));
}
function createPredicates(o, propertyKey) {
    const keys = Object.keys(o);
    const result = [];
    for (const key of keys) {
        const factory = predicates[key];
        if (factory === undefined)
            throw new Error('unknown predicate: ' + keys[0]);
        result.push(factory(o[key], propertyKey));
    }
    return result;
}
//# sourceMappingURL=predicate.js.map